import React, {useEffect, useState} from 'react';
import {Link, Navigate, useLocation} from "react-router-dom";
import {LocalStorage} from "./utils/LocalStorage";
import {FaIcon} from "./components/FaIcon";
import {useTranslation} from 'react-i18next';
import {usePreviewContext} from './context/PreviewContext';
import {usePortalClient} from './context/PortalClientContext';
import {usePortalModules} from './hooks/usePortalModules';
import {PortalClientSwitcher} from './components/portal/PortalClientSwitcher';
import {PortalClientWelcome} from './components/portal/PortalClientWelcome';
import {PortalLanguageMenu} from './components/portal/PortalLanguageMenu';
import {currentPortalLanguage, pendingUserLanguage} from './components/portal/portalLanguage';
import logo from './images/logo_dashbo_purple.svg';

const darkenHex = (hex, percent) => {
    const n = parseInt(hex.slice(1), 16);
    const d = Math.round(2.55 * percent);
    return '#' + [n >> 16, (n >> 8) & 0xFF, n & 0xFF]
        .map(c => Math.max(0, c - d).toString(16).padStart(2, '0')).join('');
};

// Navegacion: Tablero, Reportes y Connect. Cada item se filtra contra los
// modules del user (devueltos por AccessService.GetUserModules). Para users
// tipo Client el backend mapea client_can_use_dashboard → MARKETING,
// client_can_use_reports → REPORTS, client_can_use_connect → DASHBO_MCP.
const PORTAL_NAV_ITEMS = [
    {suffix: '/connections', label: 'portal.nav.connections', icon: 'plug', module: 'CLIENT_CONNECTIONS'},
    {suffix: '', label: 'portal.nav.dashboard', icon: 'chart-pie', exact: true, module: 'MARKETING'},
    {suffix: '/reports', label: 'portal.nav.reports', icon: 'file-lines', module: 'REPORTS'},
    {suffix: '/memory', label: 'portal.nav.memory', icon: 'brain', module: 'CLIENT_MEMORY'},
    {suffix: '/dashbo-mcp', label: 'portal.nav.connect', icon: 'microchip', module: 'DASHBO_MCP'},
];

export const LayoutPortal = ({children}) => {
    const {t, i18n} = useTranslation();
    const location = useLocation();
    const {isPreview, previewClientId, previewBranding} = usePreviewContext();
    const portalClient = usePortalClient();
    const modules = usePortalModules();
    const [loggedOut, setLoggedOut] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    // El idioma del usuario se aplica ANTES de dibujar el contenido: el login lo guarda pero no lo
    // aplica, y dibujar en el idioma viejo para cambiarlo enseguida haría remontar (y volver a
    // pedir) todo lo de la pantalla.
    const [languageToApply] = useState(() => pendingUserLanguage(i18n, LocalStorage.fetchUserLanguage()));
    const [languageApplied, setLanguageApplied] = useState(languageToApply === null);

    useEffect(() => {
        if (languageToApply === null) return;
        // Se dibuja igual si el cambio falla: un idioma que no se pudo aplicar no deja el portal en blanco.
        const markApplied = () => setLanguageApplied(true);
        Promise.resolve(i18n.changeLanguage(languageToApply)).then(markApplied, markApplied);
    }, [i18n, languageToApply]);

    const basePath = isPreview ? `/client/${previewClientId}/preview` : '/portal';
    const showLogout = !isPreview;

    // Branding/tema del cliente ACTIVO (multi-cliente): del contexto, con fallback a las
    // claves escalares del login (sesiones viejas / backend previo al deploy).
    const activeBranding = portalClient.activeBranding;
    const agencyLogoUrl = isPreview ? previewBranding?.logo_url
        : (activeBranding?.logo_url ?? LocalStorage.fetchAgencyLogoUrl());
    const agencyDisplayName = isPreview ? previewBranding?.display_name_for_clients
        : (activeBranding?.display_name ?? LocalStorage.fetchAgencyDisplayName());

    const brandPrimaryColor = isPreview ? previewBranding?.brand_primary_color
        : (activeBranding?.brand_primary_color ?? LocalStorage.fetchBrandPrimaryColor());
    const brandSecondaryColor = isPreview ? previewBranding?.brand_secondary_color
        : (activeBranding?.brand_secondary_color ?? LocalStorage.fetchBrandSecondaryColor());
    // Template de diseño del portal: editorial | minimal | immersive (default immersive).
    const portalTheme = ((isPreview ? previewBranding?.portal_theme
        : (activeBranding?.portal_theme ?? LocalStorage.fetchPortalTheme())) || 'IMMERSIVE').toLowerCase();
    const portalStyle = {};
    if (brandPrimaryColor) {
        portalStyle['--portal-accent'] = brandPrimaryColor;
        portalStyle['--portal-accent-hover'] = darkenHex(brandPrimaryColor, 15);
        portalStyle['--portal-accent-bg'] = brandPrimaryColor + '0F';
        portalStyle['--portal-accent-soft'] = brandPrimaryColor + '14';   // ~8% alpha
        portalStyle['--portal-accent-softer'] = brandPrimaryColor + '08'; // ~3% alpha
        portalStyle['--portal-accent-dark'] = darkenHex(brandPrimaryColor, 25);
    }
    if (brandSecondaryColor) {
        portalStyle['--portal-accent-secondary'] = brandSecondaryColor;
        portalStyle['--portal-accent-secondary-soft'] = brandSecondaryColor + '14';
    }

    // Mientras modules === null (loading inicial del fetch) no renderizamos items
    // para evitar el flash del nav completo que después se filtra. Con la bienvenida
    // multi-cliente pendiente de elección, el nav tampoco se muestra.
    const filteredItems = (modules && !portalClient.needsClientChoice)
        ? PORTAL_NAV_ITEMS.filter(item => modules.includes(item.module))
        : [];

    const navItems = filteredItems.map(item => ({
        ...item,
        path: basePath + item.suffix,
    }));

    const handleLogout = () => {
        LocalStorage.removeUserData();
        setLoggedOut(true);
    };

    if (loggedOut) {
        return <Navigate to="/login" />;
    }

    if (!languageApplied) {
        return null;
    }

    // Guard de módulos del cliente ACTIVO: si la sección actual no existe para el cliente
    // al que se acaba de switchear (o al que se entró por URL directa), volvemos al home,
    // donde la cascada de PortalHome resuelve el mejor destino. Cierra de paso el gap de
    // /portal/dashbo-mcp accesible por URL sin la capability.
    if (!isPreview && modules && !portalClient.needsClientChoice && location.pathname !== basePath) {
        const currentItem = PORTAL_NAV_ITEMS
            .filter(item => item.suffix)
            .find(item => location.pathname.startsWith(basePath + item.suffix));
        if (currentItem && !modules.includes(currentItem.module)) {
            return <Navigate to={basePath} replace />;
        }
    }

    const isActive = (item) => {
        if (item.exact) return location.pathname === item.path;
        return location.pathname.startsWith(item.path);
    };

    return (
        <div className="portal-layout" data-portal-theme={portalTheme} style={portalStyle}>
            <header className="portal-topbar">
                <div className="portal-topbar-inner">
                    <div className="portal-topbar-left">
                        <Link to={basePath} className="portal-logo-link">
                            {agencyLogoUrl ? (
                                <img src={agencyLogoUrl} className="portal-logo" alt={agencyDisplayName || 'Logo'} />
                            ) : (
                                <img src={logo} className="portal-logo" alt="Dashbo" />
                            )}
                            <span className="portal-logo-branding">
                                {agencyDisplayName ? (
                                    <span className="portal-logo-agency-name">{agencyDisplayName}</span>
                                ) : (
                                    <span className="portal-logo-agency-name">Dashbo</span>
                                )}
                            </span>
                        </Link>
                        <PortalClientSwitcher />
                    </div>

                    <nav className="portal-nav portal-nav-desktop">
                        {navItems.map(item => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`portal-nav-item ${isActive(item) ? '-active' : ''}`}
                                title={t(item.label)}
                            >
                                <FaIcon iconName={item.icon} />
                                <span className="portal-nav-label">{t(item.label)}</span>
                            </Link>
                        ))}
                    </nav>

                    <div className="portal-topbar-right">
                        {/* Como el logout, sólo para el usuario del portal: en «Ver portal como
                            cliente» quien mira es la agencia, y cambiaría su propio idioma. */}
                        {showLogout && <PortalLanguageMenu />}
                        {showLogout && (
                            <button onClick={handleLogout} className="portal-logout-btn" title={t('portal.nav.logout')}>
                                <FaIcon iconName="power-off" />
                            </button>
                        )}

                        <button
                            className="portal-hamburger"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Menu"
                        >
                            <FaIcon iconName={menuOpen ? 'xmark' : 'bars'} />
                        </button>
                    </div>
                </div>
            </header>

            {menuOpen && (
                <nav className="portal-nav-mobile">
                    {!isPreview && portalClient.isMultiClient && !portalClient.needsClientChoice && (
                        <div className="portal-nav-mobile-clients">
                            {portalClient.clients.map(client => (
                                <button
                                    key={client.id}
                                    className={`portal-nav-mobile-item ${portalClient.activeClientId === client.id ? '-active' : ''}`}
                                    onClick={() => {
                                        portalClient.setActiveClient(client.id);
                                        setMenuOpen(false);
                                    }}
                                >
                                    <FaIcon iconName={portalClient.activeClientId === client.id ? 'check' : 'building'} />
                                    <span>{client.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {navItems.map(item => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`portal-nav-mobile-item ${isActive(item) ? '-active' : ''}`}
                            onClick={() => setMenuOpen(false)}
                        >
                            <FaIcon iconName={item.icon} />
                            <span>{t(item.label)}</span>
                        </Link>
                    ))}
                    {showLogout && (
                        <PortalLanguageMenu variant="mobile" onChanged={() => setMenuOpen(false)} />
                    )}
                    {showLogout && (
                        <button
                            className="portal-nav-mobile-item"
                            onClick={handleLogout}
                        >
                            <FaIcon iconName="power-off" />
                            <span>{t('portal.nav.logout')}</span>
                        </button>
                    )}
                </nav>
            )}

            {/* key por cliente activo: cambiar de cliente remonta el contenido (cada
                pantalla re-fetchea sola) sin hard reload — el chrome no parpadea. Y por
                idioma: las celdas del Tablero que traducen con la instancia de i18next no se
                enteran solas de que el usuario lo cambió. */}
            <main className="portal-main" key={`${portalClient.activeClientId || 'portal'}-${currentPortalLanguage(i18n)}`}>
                <div className="portal-container">
                    {portalClient.needsClientChoice ? <PortalClientWelcome /> : children}
                </div>
            </main>
        </div>
    );
};
